import { useEffect, useMemo, useState } from "react";
import type { BarConfig } from "../../../components/Charts";
import chartDataFetcher, { type P4RawDataPoint } from "../../../services/api/ChartDataFetch";
import {
  CHART_DIMENSION_PALETTE,
  CHART_SEMANTIC_CORRECT,
  CHART_SEMANTIC_INCORRECT,
  CHART_SEMANTIC_MODERATE,
} from "../../../constants/chartColors";

export type P4DesajustLevel = "baix" | "moderat" | "alt";

export interface P4FilterValues {
  titulacions: string[];
  desajustLevels: P4DesajustLevel[];
}

export interface P4ChartPoint extends Record<string, string | number> {
  nom_titulacio: string;
  taxa_ocupacio_pct: number;
  taxa_adequacio_pct: number;
  taxa_sobrequalificacio_pct: number;
  index_desajust: number;
}

const BARS: BarConfig[] = [
  { key: "index_desajust", label: "Índex de desajust", color: CHART_DIMENSION_PALETTE[0] },
];

export const P4_DESAJUST_LEVEL_OPTIONS: { value: P4DesajustLevel; label: string }[] = [
  { value: "baix", label: "Baix" },
  { value: "moderat", label: "Moderat" },
  { value: "alt", label: "Alt" },
];

export const P4_DESAJUST_PERCENTILES = {
  p33: 0.153735,
  p66: 0.230317,
};

export const getP4DesajustLevel = (value: number): P4DesajustLevel => {
  if (value < P4_DESAJUST_PERCENTILES.p33) return "baix";
  if (value < P4_DESAJUST_PERCENTILES.p66) return "moderat";
  return "alt";
};

export const getP4DesajustColor = (value: number) => {
  const level = getP4DesajustLevel(value);

  if (level === "baix") return CHART_SEMANTIC_CORRECT;
  if (level === "moderat") return CHART_SEMANTIC_MODERATE;
  return CHART_SEMANTIC_INCORRECT;
};

function transformData(
  raw: P4RawDataPoint[],
  titulacions: string[],
  desajustLevels: P4DesajustLevel[]
): P4ChartPoint[] {
  if (titulacions.length === 0) return [];

  const filtered = raw.filter((d) => {
    const matchesTitulacio = titulacions.includes(d.nom_titulacio);
    const matchesLevel =
      desajustLevels.length === 0 ||
      desajustLevels.includes(getP4DesajustLevel(d.index_desajust));

    return matchesTitulacio && matchesLevel;
  });

  return filtered
    .map((d) => ({
      nom_titulacio: d.nom_titulacio,
      taxa_ocupacio_pct: d.taxa_ocupacio_pct,
      taxa_adequacio_pct: d.taxa_adequacio_pct,
      taxa_sobrequalificacio_pct: d.taxa_sobrequalificacio_pct,
      index_desajust: d.index_desajust,
    }))
    .sort((a, b) => b.index_desajust - a.index_desajust);
}

function getDefaultTitulacions(raw: P4RawDataPoint[]): string[] {
  const sorted = [...raw]
    .filter((point) => Number.isFinite(point.index_desajust))
    .sort((a, b) => b.index_desajust - a.index_desajust);

  const highest = sorted[0]?.nom_titulacio;
  const lowest = [...sorted].reverse().find((point) => point.nom_titulacio !== highest)?.nom_titulacio;

  return [highest, lowest].filter((value): value is string => Boolean(value));
}

export function useP4Data(filters: P4FilterValues) {
  const [rawData, setRawData] = useState<P4RawDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    chartDataFetcher
      .getP4Data()
      .then((data) => { if (!cancelled) setRawData(data); })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Error carregant dades");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const availableTitulacions = useMemo(
    () => Array.from(new Set(rawData.map((d) => d.nom_titulacio))).sort(),
    [rawData]
  );

  const defaultTitulacions = useMemo(
    () => getDefaultTitulacions(rawData),
    [rawData]
  );

  const chartData = useMemo(
    () => transformData(rawData, filters.titulacions, filters.desajustLevels),
    [rawData, filters.titulacions, filters.desajustLevels]
  );

  return { chartData, bars: BARS, availableTitulacions, defaultTitulacions, loading, error };
}
